import { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Check, ChevronRight } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Card, CardAction, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';

/** How much a task needs the vendor — the only colour on the row. */
export type TaskTone = 'bad' | 'watch' | 'neutral' | 'good';

export interface TaskItem {
  id: string;
  tone: TaskTone;
  /** What needs doing, in one line: "“Do you deliver to Marseille?” — asked 9 times". */
  title: ReactNode;
  /** Why, or what came of it, in grey. */
  detail?: ReactNode;
  /** A small figure at the end: a count, a time. */
  meta?: ReactNode;
  /** Where the row itself goes. */
  href?: string;
  onClick?: () => void;
  /** A second, explicit thing to do — "Ask Ardi". Shown instead of the chevron. */
  action?: { label: ReactNode; href?: string; onClick?: () => void };
}

const DOT: Record<TaskTone, string> = {
  bad: 'bg-destructive',
  watch: 'bg-warning',
  neutral: 'bg-muted-foreground/50',
  good: 'bg-success',
};

/**
 * What is waiting for the vendor, as a short to-do list: a dot, a line, where
 * to go. It stays small on purpose — a handful of rows and "N more" — and when
 * there is nothing to do it says so in one calm line rather than an
 * illustration.
 */
export function TaskList({
  title,
  description,
  items,
  loading,
  max = 5,
  emptyTitle,
  emptyText,
  more,
  className,
  testId,
}: {
  title: ReactNode;
  description?: ReactNode;
  items: TaskItem[];
  loading?: boolean;
  /** Rows shown before the "more" line. */
  max?: number;
  emptyTitle: ReactNode;
  emptyText?: ReactNode;
  /** The way to the full list: shown when there are more rows than `max`, or always when `always` is set. */
  more?: { label: (hidden: number) => ReactNode; href?: string; onClick?: () => void; always?: boolean };
  className?: string;
  testId?: string;
}) {
  const shown = items.slice(0, max);
  const hidden = Math.max(0, items.length - shown.length);

  return (
    <Card className={cn('gap-3', className)} data-testid={testId}>
      <CardHeader>
        <CardTitle className="text-sm">{title}</CardTitle>
        {description && <CardDescription className="text-xs">{description}</CardDescription>}
        {!loading && items.length > 0 && (
          <CardAction>
            <span className="rounded-md border px-2 py-0.5 text-xs tabular-nums text-muted-foreground">{items.length}</span>
          </CardAction>
        )}
      </CardHeader>
      <CardContent className="px-0">
        {loading ? (
          <div className="flex flex-col gap-2 px-6">
            <Skeleton className="h-9 w-full" />
            <Skeleton className="h-9 w-full" />
            <Skeleton className="h-9 w-2/3" />
          </div>
        ) : !items.length ? (
          <div className="flex items-start gap-3 px-6 py-1" data-testid={testId ? `${testId}-empty` : undefined}>
            <span className="mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full border">
              <Check className="size-3 text-success" />
            </span>
            <div className="min-w-0">
              <p className="text-sm">{emptyTitle}</p>
              {emptyText && <p className="mt-0.5 text-xs text-muted-foreground">{emptyText}</p>}
            </div>
          </div>
        ) : (
          <ul className="divide-y">
            {shown.map((item) => (
              <li key={item.id} className="flex items-center gap-3 px-6" data-tone={item.tone}>
                <TaskBody item={item} />
                {item.meta && <span className="shrink-0 text-xs tabular-nums text-muted-foreground">{item.meta}</span>}
                {item.action ? (
                  <TaskAction action={item.action} />
                ) : (item.href || item.onClick) ? (
                  <ChevronRight className="size-4 shrink-0 text-muted-foreground rtl:rotate-180" aria-hidden />
                ) : null}
              </li>
            ))}
          </ul>
        )}
        {!loading && more && (hidden > 0 || more.always) && (
          <div className="px-6 pt-2">
            <MoreLink more={more} hidden={hidden} />
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function TaskBody({ item }: { item: TaskItem }) {
  const interactive = !!(item.href || item.onClick);
  const body = (
    <>
      <span className={cn('mt-1.5 size-2 shrink-0 rounded-full', DOT[item.tone])} aria-hidden />
      <span className="min-w-0 flex-1">
        <span className={cn('line-clamp-2 block text-sm leading-snug', interactive && 'underline-offset-2 group-hover/task:underline')}>
          {item.title}
        </span>
        {item.detail && <span className="mt-0.5 line-clamp-1 block text-xs text-muted-foreground">{item.detail}</span>}
      </span>
    </>
  );
  const cls = 'group/task flex min-w-0 flex-1 items-start gap-3 rounded-md py-3 text-start focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none';
  if (item.href) {
    return (
      <Link to={item.href} className={cls}>
        {body}
      </Link>
    );
  }
  if (item.onClick) {
    return (
      <button type="button" onClick={item.onClick} className={cls}>
        {body}
      </button>
    );
  }
  return <div className={cls}>{body}</div>;
}

function TaskAction({ action }: { action: NonNullable<TaskItem['action']> }) {
  const cls =
    'inline-flex shrink-0 items-center gap-1 rounded-md border px-2 py-1 text-xs font-medium text-foreground hover:bg-accent focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none';
  if (action.href) {
    return (
      <Link to={action.href} className={cls}>
        {action.label}
      </Link>
    );
  }
  return (
    <button type="button" onClick={action.onClick} className={cls}>
      {action.label}
    </button>
  );
}

function MoreLink({ more, hidden }: { more: NonNullable<Parameters<typeof TaskList>[0]['more']>; hidden: number }) {
  const cls = 'inline-flex items-center gap-1 text-xs font-medium text-muted-foreground hover:text-foreground';
  const body = (
    <>
      {more.label(hidden)}
      <ArrowRight className="size-3.5 rtl:rotate-180" />
    </>
  );
  if (more.href) {
    return (
      <Link to={more.href} className={cls}>
        {body}
      </Link>
    );
  }
  return (
    <button type="button" onClick={more.onClick} className={cls}>
      {body}
    </button>
  );
}
